import type { Props } from '#~/audio/components/add/index.js';
import { Add } from '#~/audio/components/add/index.js';
import { Settings } from '#~/audio/components/settings/index.js';
import { Slicer, useDepot } from '#~/depot/hooks.js';
import type { Device } from '#~/device/index.js';
import { isAudioDevice } from '#~/device/index.js';
import { useRoom } from '#~/room/hooks.js';
import { usePopupStore } from '#~/store/popup.js';
import type { Api } from '@studio/design-system/dialog';
import { useDialog } from '@studio/design-system/dialog';
import { useLatestFn } from '@studio/react-utils/hooks';
import { pick } from 'lodash-es';
import type { ReactElement } from 'react';
import { createElement, forwardRef } from 'react';
import { shallow } from 'zustand/shallow';

// @internal
const AddAudio = forwardRef<Api, Omit<Props, 'agentIds' | 'appId' | 'devices' | 'discoveries'>>(
  ({ roomId, ...props }, forwardedRef): ReactElement => {
    const passthru = useDepot(
      (µ) => ({
        ...pick(new Slicer(µ).room(roomId), ['agentIds', 'devices', 'discoveries']),
        appId: µ.appId
      }),
      shallow
    );

    return createElement(Add, { roomId, ...props, ...passthru, ref: forwardedRef });
  }
);

/**
 * Hook that returns a function to imperatively start the 'add audio' workflow.
 *
 * @returns Imperative function.
 */
export const useAddAudio = (): (() => void) => {
  const dialog = useDialog(AddAudio);
  const roomId = useRoom((µ) => µ.id);
  const { hidePopup } = usePopupStore();

  return useLatestFn(() => {
    hidePopup();
    dialog.open({ roomId });
  });
};

/**
 * Hook that returns a function to imperatively open the audio 'advanced settings' panel.
 *
 * @returns Imperative function.
 */
export const useSettings = (): ((device: Device) => void) => {
  const dialog = useDialog(Settings);
  const { hidePopup } = usePopupStore();

  return useLatestFn((device: Device) => {
    if (isAudioDevice(device)) {
      hidePopup();
      dialog.open({ device });
    }
  });
};
